/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

import React from 'react'
import { useMemo, useUniqueId } from 'sb-hooks'

import Text from 'components/_new/typography/Text/Text'
import Svg, { SvgName, SvgColor } from 'components/_new/ui/Svg/Svg'
import Image, { ImageProps } from 'components/_new/ui/Image/Image'
import { Button } from 'components/_new/controls'
import type { ButtonProps } from 'components/_new/controls'

import ModalBase, { ModalBaseProps } from './ModalBase'

import s from './Modal.scss'


type ModalButtonProps = Omit<ButtonProps, 'size' | 'color'>

export type ModalProps = Omit<ModalBaseProps, 'ariaLabelledBy' | 'ariaDescribedby'> & {
  className?: string
  image?: string | ImageProps
  iconName?: SvgName
  iconColor?: SvgColor
  title?: string | ReactIntlMessage
  text?: string | ReactIntlMessage
  content?: React.ReactNode
  primaryButton?: ModalButtonProps
  secondaryButton?: ModalButtonProps
  ordinaryButton?: ModalButtonProps
}

const Modal: React.FunctionComponent<ModalProps> = (props) => {
  const {
    children, className,
    image, iconName, iconColor, title, text, content,
    primaryButton, secondaryButton, ordinaryButton,
    overlayClosable, htmlRole, dataTestId, onClose,
  } = props

  const uniqueId = useUniqueId()
  const titleId  = `title_${uniqueId}`
  const textId   = `text_${uniqueId}`

  const isButtonSectionVisible = primaryButton || secondaryButton || ordinaryButton

  const imageProps = useMemo<{ src: string }>(() => {
    if (typeof image === 'string') {
      return { src: image, x2: true }
    }

    return image
  }, [ image ])

  return (
    <ModalBase
      className={className}
      ariaLabelledBy={titleId}
      ariaDescribedby={textId}
      overlayClosable={overlayClosable}
      htmlRole={htmlRole}
      dataTestId={dataTestId}
      onClose={onClose}
    >
      <div className={s.content}>
        {
          Boolean(image) && (
            <Image className={s.image} h={136} {...imageProps} resizeBy="height" />
          )
        }
        {
          Boolean(iconName && !image) && (
            <Svg className={s.image} name={iconName} color={iconColor} />
          )
        }
        {
          Boolean(title) && (
            <Text className={s.title} message={title} size="28-40" semibold html id={titleId} />
          )
        }
        {
          Boolean(text) && (
            <Text message={text} size="16-24" html color="gray-80" id={textId} />
          )
        }
        {children || content}
      </div>
      {
        isButtonSectionVisible && (
          <div className={s.buttons}>
            {
              primaryButton && (
                <Button
                  dataTestId="modalPrimaryButton"
                  {...primaryButton}
                  className={s.button}
                  fullWidthOnMobile
                  color="brand"
                  size="md"
                />
              )
            }
            {
              secondaryButton && (
                <Button
                  dataTestId="modalSecondaryButton"
                  {...secondaryButton}
                  className={s.button}
                  fullWidthOnMobile
                  color="brandBorder"
                  size="md"
                />
              )
            }
            {
              ordinaryButton && (
                <Button
                  dataTestId="modalOrdinaryButton"
                  {...ordinaryButton}
                  className={s.button}
                  fullWidthOnMobile
                  color="transparent"
                  size="md"
                />
              )
            }
          </div>
        )
      }
    </ModalBase>
  )
}


export default Modal
